import * as React from 'react';
import { userImageSrc } from 'src/lib/helpers';

const TABLE_WIDTH = 114;
const TABLE_HEIGHT = 200;
// Label baseline distance from the table's bottom edge
const TABLE_LABEL_OFFSET_BOTTOM = 29;

export interface TableDef {
  id: number;
  x: number;
  y: number;
  width?: number;
  height?: number;
  image: string;
  label: string;
  // cover: image fills the whole table (dimmed); emblem: image sits on the left, label on the right
  variant?: 'cover' | 'emblem';
}

export interface FilledTable {
  id: number;
  user: Odoo.User | null;
}

export interface IRoomProps {
  activeTableId?: number | null;
  filledTables?: Record<number, FilledTable>;
  onTableClick?: (tableId: number) => void;
  standingTableIds?: Set<number>;
  style?: React.CSSProperties;
}

const EMBLEM_PADDING = 8;
const EMBLEM_SIZE = 60;
const EMBLEM_LABEL_FONT_SIZE = 14;
// The blurred backdrop overflows the table so the blur doesn't fade out at its edges
const EMBLEM_BACKDROP_BLEED = 24;
const EMBLEM_BACKDROP_BLUR = 10;
// Booked user's portrait on the left, as a share of the table height, and where its fade-out starts
const EMBLEM_AVATAR_WIDTH_RATIO = 0.78;
const EMBLEM_AVATAR_FADE_START = 0.7;

const STANDING_BADGE_RADIUS = 12;
const STANDING_BADGE_INSET = STANDING_BADGE_RADIUS + 6;

// Height-adjustable desk: desk on a column with an up/down arrow, drawn in a 24x24 box
const StandingBadge: React.FC<{ cx: number; cy: number }> = ({ cx, cy }) => (
  <g className="table__standing-badge">
    <title>Standing desk</title>
    <circle cx={cx} cy={cy} r={STANDING_BADGE_RADIUS} />
    <path
      transform={`translate(${cx - 8} ${cy - 8}) scale(${16 / 24})`}
      d="M3 9H15M9 9V20M5 20H13M19 4V20M16.5 6.5L19 4L21.5 6.5M16.5 17.5L19 20L21.5 17.5"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </g>
);

export const TableShape: React.FC<TableDef & { user?: Odoo.User | null; standing?: boolean }> = ({
  id,
  x,
  y,
  width = TABLE_WIDTH,
  height = TABLE_HEIGHT,
  image,
  label,
  user,
  standing,
  variant = 'cover',
  ...rest
}) => {
  const isEmblem = variant === 'emblem';
  const labelX = isEmblem ? x + width - EMBLEM_PADDING - 4 : x + width / 2;
  const labelY = isEmblem ? y + height - EMBLEM_PADDING - 20 : y + height - TABLE_LABEL_OFFSET_BOTTOM;
  const maskId = `table-mask-${id}`;
  const fadeId = `table-fade-${id}`;
  const blurId = `table-blur-${id}`;
  const labelPts = label.split('\n');
  const avatarSrc = user ? userImageSrc(user) : undefined;

  const renderImage = () => {
    if (!isEmblem) {
      return (
        <image
          className={avatarSrc ? 'table__user-image' : 'table__placeholder-image'}
          href={avatarSrc ?? image}
          x={x}
          y={y}
          width={width}
          height={height}
          preserveAspectRatio="xMidYMid slice"
        />
      );
    }
    // Stretched, blurred copy of the emblem as a light background behind everything
    const backdrop = (
      <>
        <filter id={blurId} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation={EMBLEM_BACKDROP_BLUR} />
        </filter>
        <image
          className="table__emblem-backdrop"
          href={image}
          x={x - EMBLEM_BACKDROP_BLEED}
          y={y - EMBLEM_BACKDROP_BLEED}
          width={width + 2 * EMBLEM_BACKDROP_BLEED}
          height={height + 2 * EMBLEM_BACKDROP_BLEED}
          preserveAspectRatio="none"
          filter={`url(#${blurId})`}
        />
      </>
    );
    if (avatarSrc) {
      // Full-height portrait on the left, fading out towards the label
      const avatarWidth = height * EMBLEM_AVATAR_WIDTH_RATIO;
      return (
        <>
          {backdrop}
          <linearGradient id={fadeId} x1="0" x2="1" y1="0" y2="0">
            <stop offset={EMBLEM_AVATAR_FADE_START} stopColor="#fff" stopOpacity="1" />
            <stop offset="1" stopColor="#fff" stopOpacity="0" />
          </linearGradient>
          <mask id={`${fadeId}-mask`} style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse">
            <rect x={x} y={y} width={avatarWidth} height={height} fill={`url(#${fadeId})`} />
          </mask>
          <image
            className="table__user-image"
            href={avatarSrc}
            x={x}
            y={y}
            width={avatarWidth}
            height={height}
            // Crop from the left so the face sits in the solid part, clear of the fade
            preserveAspectRatio="xMaxYMid slice"
            mask={`url(#${fadeId}-mask)`}
          />
        </>
      );
    }
    return (
      <>
        {backdrop}
        <image
        className="table__emblem-image"
        href={image}
        x={x + EMBLEM_PADDING}
        y={y + (height - EMBLEM_SIZE) / 2}
        width={EMBLEM_SIZE}
        height={EMBLEM_SIZE}
        preserveAspectRatio="xMinYMid meet"
      />
      </>
    );
  };

  return (
    <g className="table" data-table-id={id} data-variant={variant} {...rest}>
      <mask
        id={maskId}
        style={{ maskType: 'alpha' }}
        maskUnits="userSpaceOnUse"
        x={x}
        y={y}
        width={width}
        height={height}
      >
        <rect x={x} y={y} width={width} height={height} fill="#fff" />
      </mask>
      <g mask={`url(#${maskId})`}>{renderImage()}</g>
      <rect
        className="table__outline"
        x={x + 1}
        y={y + 1}
        width={width - 2}
        height={height - 2}
        stroke="var(--color-panel-border)"
        strokeWidth="4"
        strokeDasharray={`${width - 2} ${(width + height) * 2}`}
        strokeDashoffset="0"
      />
      {standing && <StandingBadge cx={x + width - STANDING_BADGE_INSET} cy={y + STANDING_BADGE_INSET} />}
      <text
        x={labelX}
        y={labelY}
        fill="currentColor"
        stroke="var(--color-panel-background)"
        strokeWidth="3"
        paintOrder="stroke"
        fontFamily="Roboto, sans-serif"
        fontSize={isEmblem ? EMBLEM_LABEL_FONT_SIZE : 19}
        fontWeight="700"
        textAnchor={isEmblem ? 'end' : 'middle'}
        dominantBaseline="middle"
      >
        {labelPts.length === 1
          ? labelPts[0]
          : labelPts.map((line, i) => (
              <tspan key={i} x={labelX} dy={i === 0 ? '-0.5em' : '1em'}>
                {line}
              </tspan>
            ))}
      </text>
    </g>
  );
};

export const findTableId = (target: EventTarget): number | null => {
  let n = target as Element | null;
  while (n != null) {
    const id = n.getAttribute?.('data-table-id');
    if (id != null) return Number(id);
    n = n.parentElement;
  }
  return null;
};

